import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCircleCheck,
  faCircleXmark,
} from '@fortawesome/free-solid-svg-icons';
import DataTableComponent from '../../common/DataTable/PrimeDataTable';
import ActivateDeactivateModal from '../../common/ActivateDeactivateModal';
import axiosInstance from '../../../axiosInstance';
import { fetchExternalUsers } from './externalUsersService';
import { convertUTCToLocal } from '../../utils/dateUtils';

const ExternalUserList = ({ persona }) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [confirmActivateModal, setConfirmActivateModal] = useState({
    open: false,
    record: null,
  });
  const [paginationParams, setPaginationParams] = useState({
    limit: 10,
    offset: 0,
    sortField: 'createdAt',
    sortOrder: -1,
  });
  const [t] = useTranslation();
  const navigate = useNavigate();

  useEffect(() => {
    fetchExternalUsersData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchExternalUsersData = async () => {
    setLoading(true);
    try {
      const response = await fetchExternalUsers({
        limit: paginationParams.limit,
        offset: paginationParams.offset,
        sortField: paginationParams.sortField,
        sortOrder: paginationParams.sortOrder,
      });
      setData(response || []);
    } catch (error) {
      console.error('Error fetching external users:', error);
    } finally {
      setLoading(false);
    }
  };

  const onDataTableEvent = (event) => {
    setPaginationParams((prev) => ({
      ...prev,
      limit: event.rows,
      offset: event.first,
      sortField: event.sortField || prev.sortField,
      sortOrder: event.sortOrder || prev.sortOrder,
    }));
  };

  const columns = [
    { field: 'id', header: t('ID'), sortable: true },
    {
      field: 'username',
      header: t('Username'),
      sortable: true,
      filter: true,
    },
    {
      field: 'email',
      header: t('Email'),
      sortable: true,
      filter: true,
    },
    // { field: 'phone', header: 'Phone', sortable: true, filter: true },
    {
      field: 'end_users_category.name',
      header: t('category'),
      sortable: true,
      filter: true,
      body: (rowData) => rowData.end_users_category?.name || 'N/A',
    },
    {
      field: 'active',
      header: t('active'),
      body: (rowData) => {
        if (rowData.active)
          return (
            <FontAwesomeIcon
              icon={faCircleCheck}
              style={{ color: '#198754' }}
            />
          );
        else
          return (
            <FontAwesomeIcon
              icon={faCircleXmark}
              style={{ color: '#ff0000' }}
            />
          );
      },
    },
    {
      field: 'createdAt',
      header: t('created_at'),
      sortable: true,
      filter: true,
      body: (rowData) => convertUTCToLocal(rowData.createdAt),
    },
  ];

  const actions = [
    {
      text: t('manage'),
      color: 'secondary',
      onClick: (row) => navigate(`/${persona}/external-users/edit/${row.id}`),
    },
    {
      text: t('Activate / Deactivate'),
      onClick: (row) => setConfirmActivateModal({ open: true, record: row }),
      color: 'primary',
      status: 'success',
    },
  ];

  return (
    <div>
      <DataTableComponent
        data={data}
        columns={columns}
        loading={loading}
        paginationParams={paginationParams}
        totalRecords={data?.length}
        onDataTableEvent={onDataTableEvent}
        enableCreate={true}
        createPath={`/${persona}/external-users/create`}
        enablePagination={true}
        enableLazyLoading={false}
        enableExport={true}
        actions={actions}
        sectionName={`ExternalUsers-${new Date().getDate()}-${new Date().getMonth() + 1}-${new Date().getFullYear()}-${new Date().getHours()}-${new Date().getMinutes()}`}
      />

      {confirmActivateModal.open && (
        <ActivateDeactivateModal
          sentData={confirmActivateModal.record}
          onEvent={setConfirmActivateModal}
          onConfirm={fetchExternalUsersData}
          apiPath="endUsers"
          axios={axiosInstance}
          columnName="username"
          dbAction="active"
        />
      )}
    </div>
  );
};

export default ExternalUserList;
